import { createContext, useContext, useMemo, useState } from 'react';
import OrderDetailsModal from './OrderDetailsModal';
import VisitDetailsModal from './VisitDetailsModal';
import StaffDetailsModal from './StaffDetailsModal';
import ListingDetailsModal from './ListingDetailsModal';
import { useFinance } from '../financeContext';

// A single provider that owns the four detail popups so any cell, anywhere on a finance page, can
// open one via useDetailModals() without threading modal state through every page.
const DetailModalsContext = createContext(null);

export function useDetailModals() {
  return useContext(DetailModalsContext);
}

export function DetailModalsProvider({ children, renderVisitActions }) {
  const { vertical, isAdmin, onOpenExternal } = useFinance();
  const [booking, setBooking] = useState(null);
  const [visit, setVisit] = useState(null);
  const [staffId, setStaffId] = useState(null);
  const [listing, setListing] = useState(null); // { id, vertical }

  const api = useMemo(() => ({
    openBooking: (b) => setBooking(b),
    openVisit: (v) => setVisit(v),
    openStaff: (id) => setStaffId(id),
    openListing: (id, v) => setListing({ id, vertical: v || vertical })
  }), [vertical]);

  return (
    <DetailModalsContext.Provider value={api}>
      {children}

      <OrderDetailsModal
        booking={booking}
        show={!!booking}
        onHide={() => setBooking(null)}
        isAdmin={isAdmin}
        onOpenExternal={onOpenExternal}
      />
      <VisitDetailsModal
        visit={visit}
        show={!!visit}
        onHide={() => setVisit(null)}
        onOpenStaff={(id) => setStaffId(id)}
        renderActions={
          renderVisitActions && (isAdmin)
            ? (v) => renderVisitActions(v, () => setVisit(null))
            : undefined
        }
      />
      <StaffDetailsModal
        staffId={staffId}
        show={!!staffId}
        onHide={() => setStaffId(null)}
        isAdmin={isAdmin}
        onOpenExternal={onOpenExternal}
      />
      <ListingDetailsModal
        itemId={listing?.id}
        vertical={listing?.vertical}
        show={!!listing}
        onHide={() => setListing(null)}
        isAdmin={isAdmin}
        onOpenExternal={onOpenExternal}
      />
    </DetailModalsContext.Provider>
  );
}
